/**
 * Canonical URL 생성 및 관리 유틸리티
 * Google Search Console의 "사용자가 선택한 표준이 없는 중복 페이지" 문제 해결을 위한 유틸리티
 */

const BASE_URL = 'https://ecodeal.kr';

/**
 * 주어진 경로에 대한 정규화된 canonical URL을 생성합니다.
 * 
 * @param path - 정규화할 경로 (예: '/login', '/store/123')
 * @returns 정규화된 canonical URL
 * 
 * 정규화 규칙:
 * - trailing slash 제거
 * - query parameters 제거
 * - fragment(#) 제거
 * - 소문자로 변환
 * - 절대 URL로 변환
 */
export const getCanonicalUrl = (path: string): string => {
  // 빈 경로는 홈페이지로 처리
  if (!path || path === '') {
    return BASE_URL + '/';
  }

  // 경로가 '/'로 시작하지 않으면 추가
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // query parameters와 fragment 제거
  const pathWithoutParams = cleanPath.split('?')[0].split('#')[0];
  
  // trailing slash 제거 (루트 경로 제외)
  const normalizedPath = pathWithoutParams === '/' 
    ? '/' 
    : pathWithoutParams.replace(/\/$/, '');
  
  // 소문자로 변환 (대소문자 구분 방지)
  const lowercasePath = normalizedPath.toLowerCase();
  
  return BASE_URL + lowercasePath;
};

/**
 * 현재 location에서 canonical URL을 추출합니다.
 * React Router의 location 객체나 window.location을 사용할 수 있습니다.
 */
export const getCurrentCanonicalUrl = (): string => {
  if (typeof window !== 'undefined') {
    return getCanonicalUrl(window.location.pathname);
  }
  
  // SSR 환경에서는 기본값 반환
  return BASE_URL + '/';
};

/**
 * 동적 페이지의 canonical URL을 생성합니다.
 * 
 * @param template - URL 템플릿 (예: '/store/:storeId/product/:productId')
 * @param params - 파라미터 객체 (예: { storeId: '123', productId: '456' })
 * @returns 생성된 canonical URL
 */
export const getDynamicCanonicalUrl = (
  template: string, 
  params: Record<string, string | number>
): string => {
  let path = template;
  
  // 템플릿의 파라미터를 실제 값으로 치환
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`:${key}`, String(value));
  });
  
  return getCanonicalUrl(path);
};

/**
 * SEO 페이지별 canonical URL 매핑
 * 정적 페이지들의 canonical URL을 미리 정의
 */
export const CANONICAL_URLS = {
  HOME: BASE_URL + '/',
  LOGIN: BASE_URL + '/login',
  SIGNUP_SELECT: BASE_URL + '/signup/select',
  BUYER_SIGNUP: BASE_URL + '/signup/buyer',
  SELLER_SIGNUP: BASE_URL + '/signup/seller',
  SEARCH: BASE_URL + '/buyer/search',
  ADDRESS_SELECT: BASE_URL + '/address-select',
  FAVORITES: BASE_URL + '/buyer/favorites',
  MY_PAGE: BASE_URL + '/buyer/my-page',
  CART: BASE_URL + '/buyer/cart',
  ORDER_HISTORY: BASE_URL + '/buyer/orders',
  NOTIFICATIONS: BASE_URL + '/buyer/notifications',
  SERVICE_INFO: BASE_URL + '/service-info',
  TERMS_POLICIES: BASE_URL + '/terms-policies',
  NOTICES: BASE_URL + '/notices',
  EVENTS: BASE_URL + '/events',
  SETTINGS: BASE_URL + '/settings',
} as const;

/**
 * 페이지별 canonical URL을 가져옵니다.
 * 정적 페이지는 미리 정의된 URL을, 동적 페이지는 생성된 URL을 반환합니다.
 */
export const getPageCanonicalUrl = (pathname: string): string => {
  // 정적 페이지들의 경우 미리 정의된 URL 반환
  const staticPageUrl = Object.values(CANONICAL_URLS).find(url => 
    url === BASE_URL + pathname
  );
  
  if (staticPageUrl) {
    return staticPageUrl;
  }
  
  // 동적 페이지의 경우 경로 기반으로 생성
  return getCanonicalUrl(pathname);
};

/**
 * 매장 상세 페이지 canonical URL 생성
 */
export const getStoreCanonicalUrl = (storeId: string): string => {
  return getDynamicCanonicalUrl('/store/:storeId', { storeId });
};

/**
 * 상품 상세 페이지 canonical URL 생성
 */
export const getProductCanonicalUrl = (storeId: string, productId: string): string => {
  return getDynamicCanonicalUrl('/store/:storeId/product/:productId', { 
    storeId, 
    productId 
  });
};

/**
 * 매장 리뷰 페이지 canonical URL 생성
 */
export const getStoreReviewsCanonicalUrl = (storeId: string): string => {
  return getDynamicCanonicalUrl('/store/:storeId/reviews', { storeId });
};

/**
 * 이벤트 상세 페이지 canonical URL 생성
 */
export const getEventCanonicalUrl = (eventId: string): string => {
  return getDynamicCanonicalUrl('/events/:eventId', { eventId });
};